import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "./axios";
import {
  notificationBaseURL,
  testBaseURL,
  trainingBaseURL,
  trainingBaseURLV2,
  userBaseURL,
} from "./config";

type QueryParams = Record<string, string | number | boolean | null | undefined>;
type RequestConfig = AxiosRequestConfig;
type Payload = unknown;

const JSON_HEADERS = { "Content-Type": "application/json" };
const FORM_HEADERS = { "Content-Type": "multipart/form-data" };

const withHeaders = (headers: Record<string, string>, requestConfig: RequestConfig): RequestConfig => ({
  ...requestConfig,
  headers: {
    ...headers,
    ...(requestConfig.headers ?? {}),
  },
});

const buildUrl = (baseURL: string, path: string, query?: QueryParams): string => {
  const normalizedBase = baseURL.replace(/\/+$/, "");
  const normalizedPath = String(path ?? "").replace(/^\/+/, "");
  const queryString = query ? new URLSearchParams(query as Record<string, string>).toString() : "";
  const fullPath = `${normalizedBase}/${normalizedPath}`;
  return queryString ? `${fullPath}?${queryString}` : fullPath;
};

const createApiClient = (baseURL: string) => ({
  get: (path: string, query: QueryParams = {}, requestConfig: RequestConfig = {}) =>
    axios.get(buildUrl(baseURL, path, query), requestConfig),
  postJson: (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
    axios.post(buildUrl(baseURL, path), data, withHeaders(JSON_HEADERS, requestConfig)),
  postForm: (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
    axios.post(buildUrl(baseURL, path), data, withHeaders(FORM_HEADERS, requestConfig)),
  put: (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
    axios.put(buildUrl(baseURL, path), data, requestConfig),
  patch: (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
    axios.patch(buildUrl(baseURL, path), data, requestConfig),
  delete: (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
    axios.delete(buildUrl(baseURL, path), { ...requestConfig, data }),
});

const userApi = createApiClient(userBaseURL);
const trainingApi = createApiClient(trainingBaseURL);
const trainingApiV2 = createApiClient(trainingBaseURLV2);
const testApi = createApiClient(testBaseURL);
const notificationApi = createApiClient(notificationBaseURL);

export const getData = (path: string, query: QueryParams = {}, requestConfig: RequestConfig = {}) =>
  userApi.get(path, query, requestConfig);
export const jsonPostData = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApi.postJson(path, data, requestConfig);
export const formPostData = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApi.postForm(path, data, requestConfig);
export const putData = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApi.put(path, data, requestConfig);
export const patchData = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApi.patch(path, data, requestConfig);
export const deleteItem = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  userApi.delete(path, data, requestConfig);

export const getDataTraining = (path: string, requestConfig: RequestConfig = {}) =>
  trainingApi.get(path, {}, requestConfig);
export const jsonPostDataTraining = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApi.postJson(path, data, requestConfig);
export const jsonPostDataTrainingV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApiV2.postJson(path, data, requestConfig);
export const formPostDataTraining = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApi.postForm(path, data, requestConfig);
export const putDataTraining = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApi.put(path, data, requestConfig);
export const patchDataTraining = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApi.patch(path, data, requestConfig);
export const deleteItemTraining = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  trainingApi.delete(path, data, requestConfig);

export const getDataTest = (path: string, requestConfig: RequestConfig = {}) =>
  testApi.get(path, {}, requestConfig);
export const jsonPostDataTest = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApi.postJson(path, data, requestConfig);
export const formPostDataTest = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApi.postForm(path, data, requestConfig);
export const putDataTest = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApi.put(path, data, requestConfig);
export const patchDataTest = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApi.patch(path, data, requestConfig);
export const deleteItemTest = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  testApi.delete(path, data, requestConfig);

export const getDataNotification = (path: string, requestConfig: RequestConfig = {}) =>
  notificationApi.get(path, {}, requestConfig);
export const jsonPostDataNotification = (
  path: string,
  data: Payload,
  requestConfig: RequestConfig = {},
) => notificationApi.postJson(path, data, requestConfig);
export const formPostDataNotification = (
  path: string,
  data: Payload,
  requestConfig: RequestConfig = {},
) => notificationApi.postForm(path, data, requestConfig);
export const putDataNotification = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  notificationApi.put(path, data, requestConfig);
export const patchDataNotification = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  notificationApi.patch(path, data, requestConfig);
export const deleteItemNotification = (
  path: string,
  data: Payload = {},
  requestConfig: RequestConfig = {},
) => notificationApi.delete(path, data, requestConfig);

export const fetchComments = async (
  spotlightId: string | number,
  pageNumber: number,
  pageSize: number,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<unknown[]> => {
  const response = await getData(
    `feeds/${spotlightId}/comments`,
    {
      page: pageNumber,
      page_size: pageSize,
      feed_type: feedType,
    },
    requestConfig,
  );
  return response.data || [];
};

export const postComment = async (
  spotlightId: string | number,
  text: string,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<unknown> => {
  const response = await jsonPostData(
    `feeds/${spotlightId}/comments`,
    {
      comment: text,
      feed_type: feedType,
    },
    requestConfig,
  );
  return response.data;
};

export const updateComment = async (
  spotlightId: string | number,
  id: string | number,
  text: string,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<unknown> => {
  const response = await putData(
    `feeds/${spotlightId}/comments/${id}`,
    {
      comment: text,
      feed_type: feedType,
    },
    requestConfig,
  );
  return response.data;
};

export const deleteComment = async (
  spotlightId: string | number,
  id: string | number,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<boolean> => {
  await deleteItem(
    `feeds/${spotlightId}/comments/${id}`,
    {
      feed_type: feedType,
    },
    requestConfig,
  );
  return true;
};

export const fetchTwilioAccess = async (
  spotlightId: string | number,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<{ token: string; channel_sid: string }> => {
  const response = await getData(
    `feeds/access/token/${spotlightId}`,
    {
      feed_type: feedType,
    },
    requestConfig,
  );

  return {
    token: response.data.access_token,
    channel_sid: response.data.chatroom.twilio_channel_sid,
  };
};
